/**
 * @file src/components/UserTable/helpers.tsx
 * @exports convertTableStateToSearchTags
 * @exports formatInviteString
 * @exports normalizeSearchParam
 * @exports removeQueryParamFromRouter
 * @exports removeSearchFromQuery
 * @exports rolePrettyNameLib
 * @exports toRolePickerOptions
 * @exports visibleRoleLabels
 * @summary User table helpers: role display names, invite timestamps, and URL-query-backed filter tags.
 */
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
import { RoleEnum, roleData } from "src/const";
import { ActiveFiltersType, FilterType } from "@cerebruminc/cerebellum";
import { isUserRoleVisible } from "src/helpers/userPermissions";
import { navigateApp } from "src/lib/app-router";
import { capitalizeFirstLetter } from "src/views/helper";
import { FiltersObjectType } from "src/views/types";

dayjs.extend(duration);

export const rolePrettyNameLib = (role: string) => {
	switch (role) {
		case RoleEnum.SuperAdmin:
			return roleData.superAdmin.name;
		case RoleEnum.OrganizationAdmin:
			return roleData.admin.name;
		case RoleEnum.User:
			return roleData.user.name;
		case RoleEnum.IntegrationAdmin:
			return roleData.integrationAdmin.name;
		case RoleEnum.Processor:
			return roleData.processor.name;
		case RoleEnum.CraAdmin:
			return "CRA Admin";
		case RoleEnum.TazIntegration:
			return roleData.tazIntegration.name;
		case RoleEnum.LimitedProcessor:
			return roleData.limitedProcessor.name;
		case RoleEnum.CerebrumTeam:
			return roleData.cerebrumTeam.name;
		default:
			return role;
	}
};

interface ViewerRoleProps {
	viewerBelongsToCra?: boolean;
	viewerRole?: string | null;
	viewerRoles?: Iterable<string> | null;
}

/** Role dropdown options for an organization permission, alphabetical by display name. */
export const toRolePickerOptions = ({
	roles,
	viewerBelongsToCra,
	viewerRole,
	viewerRoles,
}: ViewerRoleProps & { roles: readonly string[] }) =>
	roles
		.filter(
			(role) =>
				role !== RoleEnum.SuperAdmin &&
				isUserRoleVisible({ roleName: role, viewerBelongsToCra, viewerRole, viewerRoles }),
		)
		.map((role) => ({ id: role, label: rolePrettyNameLib(role) }))
		.sort((left, right) => left.label.localeCompare(right.label, "en", { sensitivity: "base" }));

/** Display names for roles the viewer is allowed to see. */
export const visibleRoleLabels = ({
	roleNames,
	viewerBelongsToCra,
	viewerRole,
	viewerRoles,
}: ViewerRoleProps & { roleNames: readonly (string | null | undefined)[] }) => {
	const labels: string[] = [];
	for (const roleName of roleNames) {
		if (!roleName || !isUserRoleVisible({ roleName, viewerBelongsToCra, viewerRole, viewerRoles })) continue;
		const label = rolePrettyNameLib(roleName);
		if (!labels.includes(label)) labels.push(label);
	}
	return labels;
};

// NOTE: this was copied from Cognition - we may want to move this to a shared library
const timeSinceThen = (date: string) => {
	const then = dayjs(date);
	const now = dayjs();
	const daysAgo = dayjs.duration(now.diff(then)).asDays();
	if (daysAgo > 2) {
		const flatDays = Math.floor(daysAgo);
		return `${flatDays} days ago`;
	}
	if (daysAgo > 1) {
		return "Yesterday";
	}
	return "Today";
};

export const formatInviteString = (lastInviteSentAt: string): string => {
	return !lastInviteSentAt ? "Invited" : `Invited ${timeSinceThen(lastInviteSentAt)}`;
};

/** Normalizes TanStack search values used by multi-select filters. Tanstack returns a JSON object which we need to convert to an array. */
export const normalizeSearchParam = (value: unknown): string[] => {
	if (Array.isArray(value)) {
		return value.filter((item): item is string => typeof item === "string");
	}
	return typeof value === "string" ? [value] : [];
};

export const removeQueryParamFromRouter = ({
	paramKey,
	pathname,
	search,
}: {
	paramKey: string;
	pathname: string;
	search: Record<string, unknown>;
}) => {
	const updatedQuery = { ...search };
	delete updatedQuery[paramKey];

	void navigateApp({ replace: true, search: updatedQuery, skipRouteChange: true, to: pathname });
};

const beautifyTitle = ({ filterType, filterId }: { filterType: string; filterId: string }) => {
	switch (filterType) {
		case "roles":
		case "organizations":
			return capitalizeFirstLetter(filterType.slice(0, -1));
		case "contactName":
			return "Contact Name";
		case "contactEmail":
			return "Contact Email";
		case "name":
			return "Name";
		case "email":
			return "Email";
		default:
			return filterId;
	}
};

export const convertTableStateToSearchTags = ({
	tableState,
	availableFilters,
}: {
	tableState?: ActiveFiltersType;
	availableFilters?: FiltersObjectType;
}): FilterType[] => {
	if (!tableState) return [];
	const convertedFilters: FilterType[] = [];
	for (const [filterType, filterIdArray] of Object.entries(tableState)) {
		for (const filterId of filterIdArray as string[]) {
			const formattedTitle = beautifyTitle({ filterType, filterId });
			const searchTypeObject = {
				searchType: filterType,
				filterType: filterType,
				id: `${filterType}_${filterId}`,
				title: formattedTitle,
				label:
					filterType === "roles"
						? rolePrettyNameLib(filterId)
						: filterType === "organizations"
							? availableFilters?.organizations?.find((org: FilterType) => org.id === filterId)?.label || filterId
							: filterId,
			};
			convertedFilters.push(searchTypeObject);
		}
	}
	return convertedFilters;
};

export const removeSearchFromQuery = ({
	pathname,
	search,
	tag,
}: {
	pathname: string;
	search: Record<string, unknown>;
	tag: FilterType;
}) => {
	const queryValue = search[tag.searchType];
	if (!queryValue) return;
	let queryArray = [];
	if (tag.searchType === "name" || tag.searchType === "contactName" || tag.searchType === "contactEmail") {
		queryArray.push(queryValue as string);
	} else {
		queryArray = normalizeSearchParam(queryValue);
	}
	queryArray = queryArray.filter((item: string) => item !== tag.id.substring(tag.id.indexOf("_") + 1));

	if (queryArray.length && !tag?.idArray?.length) {
		void navigateApp({ search: { ...search, [tag.searchType]: queryArray }, to: pathname });
	} else {
		removeQueryParamFromRouter({ paramKey: tag.searchType, pathname, search });
	}
};
